import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import App from "../../Utils/LayoutMobile";

const C_PINK   = "#F04DA0";
const C_PURPLE = "#8B2FC9";
const C_DARK   = "#1a0040";
const C_GRAD   = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;

// ── helpers ──────────────────────────────────────────────────
const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

// ── CSV export — columns match BrochureLead model ──
const downloadCSV = (rows) => {
  if (!rows.length) return;
  const headers = ["#", "Full Name", "Email", "Phone Number", "Purpose / Message", "Submitted At"];
  const lines = rows.map((r, i) =>
    [i + 1, r.fullName, r.email, r.phoneNumber, r.message ?? "", fmt(r.createdAt)]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `brochure-leads-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Upload Modal Component ────────────────────────────────────
function BrochureModal({ onClose, onUploaded, existingPdfUrl }) {
  const [formData,  setFormData]  = useState({ pdf: null });
  const [errors,    setErrors]    = useState({});
  const [message,   setMessage]   = useState("");
  const [pdfUrl,    setPdfUrl]    = useState(existingPdfUrl || null);
  const [loading,   setLoading]   = useState(false);
  const [dragging,  setDragging]  = useState(false);
  const [activeTab, setActiveTab] = useState(existingPdfUrl ? "preview" : "upload");
  const fileInputRef = useRef();

  const validate = () => {
    const errs = {};
    if (!formData.pdf && !pdfUrl) errs.pdf = "A PDF file is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      setErrors(p => ({ ...p, pdf: "Only PDF files are accepted." }));
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setErrors(p => ({ ...p, pdf: "File must be 3 MB or smaller." }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFormData(p => ({ ...p, pdf: file }));
    setErrors(p => ({ ...p, pdf: "" }));
  };

  const handleChange = e => handleFile(e.target.files[0]);

  const handleDrop = e => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const removeFile = () => {
    setFormData(p => ({ ...p, pdf: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
    setErrors(p => ({ ...p, pdf: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setMessage(""); setLoading(true);

    const payload = new FormData();
    if (formData.pdf) payload.append("pdf", formData.pdf);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/brochure/upload`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data.success) {
        setMessage(data.message || "Brochure uploaded successfully!");
        const newUrl = `${data.data.pdfUrl}?t=${Date.now()}`;
        setPdfUrl(newUrl);
        setErrors({});
        if (fileInputRef.current) fileInputRef.current.value = "";
        setFormData(p => ({ ...p, pdf: null }));
        if (onUploaded) onUploaded(newUrl);
        setActiveTab("preview");
      } else {
        setMessage("Upload failed. Please try again.");
      }
    } catch (err) {
      setMessage(`Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="mb-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mb-modal">

        {/* Modal Header */}
        <div className="mb-modal-header">
          <div className="mb-modal-title-group">
            <div className="mb-modal-icon">
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div>
              <div className="mb-modal-title">Manage Brochure PDF</div>
              <div className="mb-modal-sub">Upload · PDF only · Max 3 MB</div>
            </div>
          </div>
          <button className="mb-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-tabs">
          <button className={`mb-tab ${activeTab === "upload" ? "mb-tab--active" : ""}`} onClick={() => setActiveTab("upload")}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
            </svg>
            Upload New
          </button>
          <button className={`mb-tab ${activeTab === "preview" ? "mb-tab--active" : ""}`} onClick={() => setActiveTab("preview")} disabled={!pdfUrl}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            Preview Current
            {pdfUrl && <span className="mb-tab-dot" />}
          </button>
        </div>

        {/* Tab Content */}
        <div className="mb-modal-body">
          {activeTab === "upload" ? (
            <form onSubmit={handleSubmit}>
              <div
                className={`mb-dropzone ${dragging ? "mb-dropzone--drag" : ""} ${errors.pdf ? "mb-dropzone--error" : ""} ${formData.pdf ? "mb-dropzone--filled" : ""}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
              >
                <input ref={fileInputRef} type="file" name="pdf" accept="application/pdf" onChange={handleChange} style={{ display: "none" }} />
                {formData.pdf ? (
                  <div className="mb-file-selected">
                    <div className="mb-file-icon">
                      <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      </svg>
                    </div>
                    <div className="mb-file-info">
                      <span className="mb-file-name">{formData.pdf.name}</span>
                      <span className="mb-file-size">{(formData.pdf.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <button type="button" className="mb-file-remove" onClick={e => { e.stopPropagation(); removeFile(); }}>
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="mb-dropzone-empty">
                    <div className="mb-upload-icon">
                      <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <polyline points="16 16 12 12 8 16"/>
                        <line x1="12" y1="12" x2="12" y2="21"/>
                        <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
                      </svg>
                    </div>
                    <p className="mb-dz-title">{dragging ? "Drop your PDF here" : "Drag & drop or click to upload"}</p>
                    <p className="mb-dz-hint">PDF files only · Maximum 3 MB</p>
                  </div>
                )}
              </div>

              {errors.pdf && (
                <div className="mb-field-error">
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {errors.pdf}
                </div>
              )}

              {message && (
                <div className={`mb-status-msg ${pdfUrl ? "mb-status-msg--ok" : "mb-status-msg--err"}`}>
                  {pdfUrl
                    ? <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    : <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  }
                  {message}
                </div>
              )}

              <div className="mb-modal-footer">
                {pdfUrl && (
                  <span className="mb-active-badge">
                    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    Brochure active
                  </span>
                )}
                <button type="submit" className="mb-upload-btn" disabled={loading}>
                  {loading ? (
                    <><span className="mb-btn-spinner" /> Uploading…</>
                  ) : (
                    <>
                      {pdfUrl ? "Replace Brochure" : "Upload Brochure"}
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                        <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="mb-preview-wrap">
              <div className="mb-preview-toolbar">
                <span className="mb-preview-live-dot" />
                <span className="mb-preview-live-txt">Live — visible to delegates</span>
                <a href={pdfUrl} target="_blank" rel="noreferrer" className="mb-open-link">
                  Open in new tab
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              </div>
              <iframe src={pdfUrl} title="Brochure PDF" className="mb-pdf-frame" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main ManageBrochure Page ──────────────────────────────────
export default function ManageBrochure() {
  const [showModal,     setShowModal]     = useState(false);
  const [pdfUrl,        setPdfUrl]        = useState(null);

  const [leads,         setLeads]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [search,        setSearch]        = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId,    setDeletingId]    = useState(null);
  const [viewLead,      setViewLead]      = useState(null); // <-- NEW STATE FOR VIEWING PROFILE

  // Fetch existing brochure PDF URL
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/brochure`)
      .then(({ data }) => { if (data.success && data.data) setPdfUrl(data.data.pdfUrl); })
      .catch(err => console.error("Failed to fetch brochure:", err));
  }, []);

  // Fetch brochure leads
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/brochure-leads/leads`)
      .then(({ data }) => {
        if (data.success) setLeads(data.data);
        else setError("Failed to load leads.");
      })
      .catch(() => setError("Server error — could not load leads."))
      .finally(() => setLoading(false));
  }, []);

  // Search across all visible fields
  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    return (
      l.fullName?.toLowerCase().includes(q)    ||
      l.email?.toLowerCase().includes(q)       ||
      l.phoneNumber?.includes(q)               ||
      l.message?.toLowerCase().includes(q)
    );
  });

  const todayCount = leads.filter((l) => {
    return new Date(l.createdAt).toDateString() === new Date().toDateString();
  }).length;

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/brochure-leads/leads/${id}`
      );
      setLeads((prev) => prev.filter((l) => l._id !== id));
      if (viewLead && viewLead._id === id) setViewLead(null);
    } catch (err) {
      console.error("Failed to delete lead:", err);
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  return (
    <App>
      <div className="mb-page">

        {/* ── Page Header ── */}
        <div className="mb-page-header">
          <div className="mb-header-left">
            <div className="mb-eyebrow">
              <span className="mb-eyebrow-dot" />
              Brochure Management
            </div>
            <h1 className="mb-title">
              Manage <span className="mb-grad">Brochure</span>
            </h1>
            <p className="mb-sub">
              Upload the conference brochure PDF and view all delegates who downloaded it.
            </p>
          </div>

          <button className="mb-manage-btn" onClick={() => setShowModal(true)}>
            {pdfUrl ? (
              <>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                Manage Brochure PDF
                <span className="mb-btn-live-dot" />
              </>
            ) : (
              <>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
                </svg>
                Upload Brochure PDF
              </>
            )}
          </button>
        </div>

        {/* ── Stats Row ── */}
        <div className="mb-stats-row">
          <div className="mb-stat-card">
            <div className="mb-stat-icon mb-stat-icon--total">
              <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
            </div>
            <div>
              <div className="mb-stat-value">{leads.length}</div>
              <div className="mb-stat-label">Total Downloads</div>
            </div>
          </div>

          <div className="mb-stat-card">
            <div className="mb-stat-icon mb-stat-icon--today">
              <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div>
              <div className="mb-stat-value">{todayCount}</div>
              <div className="mb-stat-label">Today</div>
            </div>
          </div>

          <div className="mb-stat-card">
            <div className="mb-stat-icon mb-stat-icon--pdf">
              <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div>
              <div className="mb-stat-value" style={{ fontSize: 13, fontWeight: 700 }}>
                {pdfUrl
                  ? <span style={{ color: "#16a34a" }}>Active</span>
                  : <span style={{ color: "#f87171" }}>Not set</span>
                }
              </div>
              <div className="mb-stat-label">PDF Status</div>
            </div>
          </div>
        </div>

        {/* ── Leads Table Card ── */}
        <div className="mb-card">
          <div className="mb-card-toolbar">
            <div className="mb-card-title-group">
              <span className="mb-card-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              </span>
              <span className="mb-card-title">Download Leads</span>
              <span className="mb-count-badge">{filtered.length}</span>
            </div>

            <div className="mb-toolbar-right">
              <div className="mb-search-wrap">
                <svg className="mb-search-ico" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  className="mb-search"
                  placeholder="Search name, email, phone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <button
                className="mb-export-btn"
                onClick={() => downloadCSV(filtered)}
                disabled={!filtered.length}
                title="Export as CSV"
              >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="mb-table-wrap">
            {loading ? (
              <div className="mb-center">
                <div className="mb-spinner" />
                <p className="mb-center-msg">Loading leads…</p>
              </div>
            ) : error ? (
              <div className="mb-center mb-center--error">
                <svg width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p className="mb-center-msg">{error}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="mb-center">
                <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ color: "#c4b4d8" }}>
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
                <p className="mb-center-msg">
                  {search ? "No results match your search." : "No leads yet. They'll appear once users fill the form."}
                </p>
              </div>
            ) : (
              <table className="mb-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Purpose / Message</th>
                    <th>Submitted At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, idx) => (
                    <tr key={lead._id}>
                      <td className="mb-td-num">{idx + 1}</td>

                      {/* Name — uses model field: fullName */}
                      <td>
                        <div className="mb-name-cell">
                          <span className="mb-avatar">
                            {lead.fullName?.charAt(0).toUpperCase() || "?"}
                          </span>
                          <span className="mb-name">{lead.fullName || "—"}</span>
                        </div>
                      </td>

                      {/* Email */}
                      <td>
                        <a href={`mailto:${lead.email}`} className="mb-link">{lead.email || "—"}</a>
                      </td>

                      {/* Phone — uses model field: phoneNumber */}
                      <td>
                        <a href={`tel:${lead.phoneNumber}`} className="mb-link">{lead.phoneNumber || "—"}</a>
                      </td>

                      {/* Message / Purpose — optional field */}
                      <td>
                        {lead.message
                          ? <span className="mb-msg-cell" title={lead.message}>{lead.message}</span>
                          : <span style={{ color: "#c4b4d8", fontSize: 12 }}>—</span>
                        }
                      </td>

                      <td className="mb-td-date">{fmt(lead.createdAt)}</td>

                      <td>
                        <div className="mb-actions-flex">
                          {/* ── View Button ── */}
                          <button
                            className="mb-icon-btn mb-view-btn"
                            onClick={() => setViewLead(lead)}
                            title="View Lead Profile"
                          >
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>

                          {/* ── Delete Button ── */}
                          <button
                            className="mb-icon-btn mb-del-btn"
                            onClick={() => setDeleteConfirm(lead)}
                            disabled={deletingId === lead._id}
                            title="Delete lead"
                          >
                            {deletingId === lead._id ? (
                              <span className="mb-del-row-spinner" />
                            ) : (
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                <path d="M10 11v6"/><path d="M14 11v6"/>
                                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── Lead Profile View Modal (NEW) ── */}
      {viewLead && (
        <div className="mb-overlay" onClick={(e) => { if (e.target === e.currentTarget) setViewLead(null); }}>
          <div className="mb-view-modal-container">
            <div className="mb-view-header">
              <h3 className="mb-view-header-title"></h3>
              <button className="mb-close-btn" onClick={() => setViewLead(null)} aria-label="Close">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <div className="mb-view-body">
              <div className="mb-view-hero">
                <div className="mb-view-avatar-large">
                  {viewLead.fullName?.charAt(0).toUpperCase() || "?"}
                </div>
                <h4 className="mb-view-name-large">{viewLead.fullName || "—"}</h4>
                <span className="mb-view-date-badge">Downloaded on {fmt(viewLead.createdAt)}</span>
              </div>

              <div className="mb-view-grid">
                <div className="mb-view-item">
                  <span className="mb-view-label">Email Address</span>
                  <span className="mb-view-val">
                    <a href={`mailto:${viewLead.email}`} className="mb-link">{viewLead.email || "—"}</a>
                  </span>
                </div>
                <div className="mb-view-item">
                  <span className="mb-view-label">Phone Number</span>
                  <span className="mb-view-val">
                    <a href={`tel:${viewLead.phoneNumber}`} className="mb-link">{viewLead.phoneNumber || "—"}</a>
                  </span>
                </div>
                
                <div className="mb-view-item mb-view-item--full">
                  <span className="mb-view-label">Purpose / Message</span>
                  <div className="mb-view-msg-box">
                    {viewLead.message ? viewLead.message : <span style={{ color: "#a0aec0" }}>No message provided.</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteConfirm && (
        <div className="mb-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDeleteConfirm(null); }}>
          <div className="mb-del-modal">
            <div className="mb-del-icon">
              <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
              </svg>
            </div>
            <h3 className="mb-del-title">Delete Lead?</h3>
            <p className="mb-del-msg">
              You're about to delete <strong>{deleteConfirm.fullName}</strong>. This action cannot be undone.
            </p>
            <div className="mb-del-btns">
              <button className="mb-del-cancel" onClick={() => setDeleteConfirm(null)} disabled={!!deletingId}>
                Cancel
              </button>
              <button className="mb-del-confirm" onClick={() => handleDelete(deleteConfirm._id)} disabled={!!deletingId}>
                {deletingId === deleteConfirm._id ? (
                  <><span className="mb-btn-spinner" /> Deleting…</>
                ) : (
                  <>
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                    </svg>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Brochure PDF Modal ── */}
      {showModal && (
        <BrochureModal
          onClose={() => setShowModal(false)}
          onUploaded={(url) => setPdfUrl(url)}
          existingPdfUrl={pdfUrl}
        />
      )}

      <style>{`
        @keyframes mb-fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes mb-slideUp { from { opacity: 0; transform: translateY(20px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        @keyframes mb-spin    { to { transform: rotate(360deg) } }
        @keyframes mb-pulse   { 0%,100%{ opacity:1 } 50%{ opacity:.35 } }

        .mb-page { padding: 28px 28px 60px; min-height: 100%; background: #f4eefa; font-family: inherit; }

        .mb-page-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 16px; margin-bottom: 24px; }
        .mb-eyebrow { display: inline-flex; align-items: center; gap: 8px; background: rgba(139,47,201,0.08); border: 1px solid rgba(139,47,201,0.18); color: ${C_PURPLE}; font-size: 11px; font-weight: 800; letter-spacing: 0.13em; text-transform: uppercase; padding: 5px 12px; border-radius: 50px; margin-bottom: 10px; width: fit-content; }
        .mb-eyebrow-dot { width: 7px; height: 7px; border-radius: 50%; background: ${C_PINK}; animation: mb-pulse 2s infinite; }
        .mb-title { font-size: clamp(22px, 3vw, 30px); font-weight: 800; color: ${C_DARK}; margin: 0 0 6px; }
        .mb-grad { background: linear-gradient(90deg, ${C_PINK}, ${C_PURPLE}); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .mb-sub { font-size: 13.5px; color: #8a7a9a; margin: 0; }

        .mb-manage-btn { display: inline-flex; align-items: center; gap: 8px; padding: 11px 20px; background: ${C_GRAD}; color: #fff; font-size: 13px; font-weight: 700; border: none; border-radius: 12px; cursor: pointer; box-shadow: 0 6px 20px rgba(240,77,160,0.3); transition: transform 0.2s, box-shadow 0.2s; white-space: nowrap; flex-shrink: 0; position: relative; margin-top: 6px; }
        .mb-manage-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(240,77,160,0.42); }
        .mb-btn-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 0 2px rgba(74,222,128,0.3); animation: mb-pulse 2s infinite; }

        .mb-stats-row { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 24px; }
        .mb-stat-card { background: #fff; border-radius: 14px; border: 1px solid #ede5f5; box-shadow: 0 2px 12px rgba(26,0,64,0.05); padding: 16px 22px; display: flex; align-items: center; gap: 14px; min-width: 160px; }
        .mb-stat-icon { width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .mb-stat-icon--total { background: rgba(240,77,160,0.1); color: ${C_PINK}; border: 1px solid rgba(240,77,160,0.2); }
        .mb-stat-icon--today { background: rgba(139,47,201,0.1); color: ${C_PURPLE}; border: 1px solid rgba(139,47,201,0.2); }
        .mb-stat-icon--pdf   { background: rgba(16,163,74,0.1); color: #16a34a; border: 1px solid rgba(16,163,74,0.2); }
        .mb-stat-value { font-size: 22px; font-weight: 800; color: ${C_DARK}; line-height: 1.1; }
        .mb-stat-label { font-size: 12px; color: #9a86b8; font-weight: 600; margin-top: 2px; }

        .mb-card { background: #fff; border-radius: 20px; border: 1px solid #ede5f5; box-shadow: 0 4px 20px rgba(26,0,64,0.06); overflow: hidden; }
        .mb-card-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 18px 22px; border-bottom: 1px solid #f3ecfa; background: #faf6fe; }
        .mb-card-title-group { display: flex; align-items: center; gap: 10px; }
        .mb-card-icon { width: 34px; height: 34px; border-radius: 9px; background: rgba(240,77,160,0.1); border: 1px solid rgba(240,77,160,0.2); display: flex; align-items: center; justify-content: center; color: ${C_PINK}; }
        .mb-card-title { font-size: 14.5px; font-weight: 800; color: ${C_DARK}; }
        .mb-count-badge { background: ${C_GRAD}; color: #fff; font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 50px; }
        .mb-toolbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .mb-search-wrap { position: relative; }
        .mb-search-ico { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9a86b8; pointer-events: none; }
        .mb-search { background: #f6f0fc; border: 1px solid #ede5f5; border-radius: 9px; padding: 8px 14px 8px 30px; font-size: 13px; font-weight: 500; color: ${C_DARK}; outline: none; width: 220px; transition: border-color 0.2s, box-shadow 0.2s; }
        .mb-search:focus { border-color: ${C_PINK}; box-shadow: 0 0 0 3px rgba(240,77,160,0.12); }
        .mb-search::placeholder { color: #b0a0c8; }

        .mb-export-btn { display: inline-flex; align-items: center; gap: 7px; padding: 8px 16px; background: ${C_GRAD}; color: #fff; font-size: 12.5px; font-weight: 700; border: none; border-radius: 9px; cursor: pointer; box-shadow: 0 4px 14px rgba(240,77,160,0.25); transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s; white-space: nowrap; }
        .mb-export-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 7px 18px rgba(240,77,160,0.38); }
        .mb-export-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .mb-table-wrap { overflow-x: auto; }
        .mb-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
        .mb-table thead tr { background: #faf6fe; border-bottom: 1px solid #f0e8fb; }
        .mb-table th { padding: 13px 18px; text-align: left; font-size: 11px; font-weight: 800; color: #9a86b8; letter-spacing: 0.08em; text-transform: uppercase; white-space: nowrap; }
        .mb-table td { padding: 13px 18px; border-bottom: 1px solid #f8f2ff; color: #3a2a5a; vertical-align: middle; }
        .mb-table tbody tr:last-child td { border-bottom: none; }
        .mb-table tbody tr:hover td { background: #fdf7ff; }
        .mb-td-num  { color: #b0a0c8; font-weight: 700; font-size: 12.5px; }
        .mb-td-date { color: #8a7a9a; font-size: 12.5px; white-space: nowrap; }

        .mb-name-cell { display: flex; align-items: center; gap: 10px; }
        .mb-avatar { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; background: ${C_GRAD}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 800; }
        .mb-name  { font-weight: 700; color: ${C_DARK}; }
        .mb-link  { color: ${C_PURPLE}; text-decoration: none; font-weight: 600; }
        .mb-link:hover { text-decoration: underline; }

        /* Message cell — truncate long text */
        .mb-msg-cell { display: block; max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 12.5px; color: #6b5f8a; }

        .mb-center { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 60px 24px; color: #c4b4d8; }
        .mb-center--error { color: #f87171; }
        .mb-center-msg { font-size: 13.5px; font-weight: 600; margin: 0; color: inherit; text-align: center; }
        .mb-spinner { width: 32px; height: 32px; border-radius: 50%; border: 3px solid rgba(240,77,160,0.2); border-top: 3px solid ${C_PINK}; animation: mb-spin 0.75s linear infinite; }

        /* ── Action Buttons ── */
        .mb-actions-flex { display: flex; align-items: center; gap: 8px; }
        .mb-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; transition: background 0.2s, border-color 0.2s, transform 0.15s; }
        .mb-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        
        .mb-view-btn { background: #f6f0fc; color: ${C_PURPLE}; border-color: #ede5f5; }
        .mb-view-btn:hover:not(:disabled) { background: #f0e4ff; border-color: #d8c4f0; transform: scale(1.08); }
        
        .mb-del-btn { background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.2); color: #f87171; }
        .mb-del-btn:hover:not(:disabled) { background: rgba(248,113,113,0.18); border-color: rgba(248,113,113,0.4); transform: scale(1.08); }
        .mb-del-row-spinner { width: 13px; height: 13px; border-radius: 50%; border: 2px solid rgba(248,113,113,0.25); border-top: 2px solid #f87171; animation: mb-spin 0.7s linear infinite; display: inline-block; }

        /* ── Modals ── */
        .mb-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(26,0,64,0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; animation: mb-fadeIn 0.2s ease; }
        
        /* Main Modal */
        .mb-modal { background: #fff; border-radius: 22px; width: 100%; max-width: 560px; box-shadow: 0 24px 64px rgba(26,0,64,0.25); overflow: hidden; animation: mb-slideUp 0.25s cubic-bezier(.16,1,.3,1); max-height: 90vh; display: flex; flex-direction: column; }
        .mb-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 22px 16px; border-bottom: 1px solid #f3ecfa; background: #faf6fe; flex-shrink: 0; }
        .mb-modal-title-group { display: flex; align-items: center; gap: 12px; }
        .mb-modal-icon { width: 42px; height: 42px; border-radius: 11px; flex-shrink: 0; background: rgba(240,77,160,0.1); border: 1px solid rgba(240,77,160,0.2); display: flex; align-items: center; justify-content: center; color: ${C_PINK}; }
        .mb-modal-title { font-size: 15px; font-weight: 800; color: ${C_DARK}; }
        .mb-modal-sub   { font-size: 11.5px; color: #9a86b8; margin-top: 2px; }
        .mb-close-btn { width: 32px; height: 32px; border-radius: 9px; background: #f6f0fc; border: 1px solid #ede5f5; display: flex; align-items: center; justify-content: center; color: #8a7a9a; cursor: pointer; transition: background 0.2s, color 0.2s; flex-shrink: 0; }
        .mb-close-btn:hover { background: #fee2e2; color: #f87171; border-color: #fecaca; }

        /* View Profile Modal */
        .mb-view-modal-container { background: #fff; border-radius: 22px; width: 100%; max-width: 480px; box-shadow: 0 24px 64px rgba(26,0,64,0.25); overflow: hidden; animation: mb-slideUp 0.25s cubic-bezier(.16,1,.3,1); display: flex; flex-direction: column; }
        .mb-view-header-title { font-size: 16px; font-weight: 800; color: ${C_DARK}; margin: 0; }
        .mb-view-hero { display: flex; flex-direction: column; align-items: center; padding: 24px 20px; background: #faf6fe; border-bottom: 1px solid #f3ecfa; }
        .mb-view-avatar-large { width: 64px; height: 64px; border-radius: 50%; background: ${C_GRAD}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 800; margin-bottom: 12px; box-shadow: 0 4px 14px rgba(240,77,160,0.25); }
        .mb-view-name-large { font-size: 18px; font-weight: 800; color: ${C_DARK}; margin: 0 0 6px; }
        .mb-view-date-badge { font-size: 11px; font-weight: 700; color: #8a7a9a; background: #ede5f5; padding: 4px 12px; border-radius: 50px; text-transform: uppercase; letter-spacing: 0.5px; }
        
        .mb-view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 24px 28px; }
        .mb-view-item { display: flex; flex-direction: column; gap: 6px; }
        .mb-view-item--full { grid-column: 1 / -1; }
        .mb-view-label { font-size: 11.5px; font-weight: 800; color: #9a86b8; text-transform: uppercase; letter-spacing: 0.5px; }
        .mb-view-val { font-size: 14px; font-weight: 600; color: #3a2a5a; }
        .mb-view-msg-box { background: #f6f0fc; border: 1px solid #ede5f5; border-radius: 12px; padding: 14px; font-size: 13.5px; font-weight: 500; color: #4a3a6b; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }

        /* Delete Modal */
        .mb-del-modal { background: #fff; border-radius: 20px; padding: 32px 28px 26px; width: 100%; max-width: 380px; text-align: center; box-shadow: 0 24px 64px rgba(26,0,64,0.22); animation: mb-slideUp 0.22s cubic-bezier(.16,1,.3,1); }
        .mb-del-icon { width: 58px; height: 58px; border-radius: 50%; background: rgba(248,113,113,0.1); border: 1.5px solid rgba(248,113,113,0.25); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; color: #f87171; }
        .mb-del-title { margin: 0 0 10px; font-size: 17px; font-weight: 800; color: ${C_DARK}; }
        .mb-del-msg   { margin: 0 0 24px; font-size: 13.5px; color: #6b5f8a; line-height: 1.6; }
        .mb-del-btns  { display: flex; gap: 12px; }
        .mb-del-cancel { flex: 1; padding: 11px; border: 1.5px solid #ede5f5; border-radius: 10px; background: #faf5ff; color: #4a3a6b; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: background 0.2s; font-family: inherit; }
        .mb-del-cancel:hover:not(:disabled) { background: #f0e4ff; }
        .mb-del-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
        .mb-del-confirm { flex: 1; padding: 11px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: none; border-radius: 10px; background: linear-gradient(135deg, #f87171, #ef4444); color: #fff; font-size: 13.5px; font-weight: 700; cursor: pointer; font-family: inherit; box-shadow: 0 4px 14px rgba(248,113,113,0.35); transition: opacity 0.2s, transform 0.2s; }
        .mb-del-confirm:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .mb-del-confirm:disabled { opacity: 0.7; cursor: not-allowed; }

        /* Shared Modal Tabs & Drops */
        .mb-tabs { display: flex; border-bottom: 1px solid #f3ecfa; padding: 0 22px; background: #fff; flex-shrink: 0; }
        .mb-tab { display: inline-flex; align-items: center; gap: 7px; padding: 12px 4px; margin-right: 20px; font-size: 12.5px; font-weight: 700; color: #9a86b8; background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; transition: color 0.2s, border-color 0.2s; position: relative; white-space: nowrap; }
        .mb-tab:hover:not(:disabled) { color: ${C_DARK}; }
        .mb-tab--active { color: ${C_PINK}; border-bottom-color: ${C_PINK}; }
        .mb-tab:disabled { opacity: 0.4; cursor: not-allowed; }
        .mb-tab-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; position: absolute; top: 10px; right: -8px; }

        .mb-modal-body { padding: 20px 22px; overflow-y: auto; flex: 1; }

        .mb-dropzone { border: 2px dashed #e0d0f0; border-radius: 14px; background: #fdfbff; cursor: pointer; transition: border-color .25s, background .25s, box-shadow .25s; min-height: 130px; display: flex; align-items: center; justify-content: center; }
        .mb-dropzone:hover     { border-color: ${C_PINK}; background: #fff8fc; }
        .mb-dropzone--drag     { border-color: ${C_PINK}; background: #fff0f8; box-shadow: 0 0 0 4px rgba(240,77,160,0.1); }
        .mb-dropzone--error    { border-color: #f87171; background: #fff5f5; }
        .mb-dropzone--filled   { border-style: solid; border-color: rgba(240,77,160,0.3); background: #fff8fc; }

        .mb-dropzone-empty { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 28px 20px; text-align: center; }
        .mb-upload-icon { width: 50px; height: 50px; border-radius: 13px; background: rgba(240,77,160,0.08); border: 1px solid rgba(240,77,160,0.15); display: flex; align-items: center; justify-content: center; color: ${C_PINK}; margin-bottom: 4px; }
        .mb-dz-title { font-size: 13.5px; font-weight: 700; color: ${C_DARK}; margin: 0; }
        .mb-dz-hint  { font-size: 11.5px; color: #9a86b8; margin: 0; }

        .mb-file-selected { display: flex; align-items: center; gap: 12px; padding: 16px 18px; width: 100%; }
        .mb-file-icon { width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0; background: rgba(240,77,160,0.1); border: 1px solid rgba(240,77,160,0.2); display: flex; align-items: center; justify-content: center; color: ${C_PINK}; }
        .mb-file-info { flex: 1; min-width: 0; }
        .mb-file-name { display: block; font-size: 13.5px; font-weight: 700; color: ${C_DARK}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mb-file-size { font-size: 11.5px; color: #9a86b8; margin-top: 2px; display: block; }
        .mb-file-remove { width: 28px; height: 28px; border-radius: 7px; border: none; background: #fff0f8; color: #f87171; flex-shrink: 0; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s; }
        .mb-file-remove:hover { background: #fee2e2; }

        .mb-field-error { display: flex; align-items: center; gap: 6px; margin-top: 8px; font-size: 12px; font-weight: 600; color: #dc2626; }
        .mb-status-msg { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 10px 14px; border-radius: 10px; font-size: 12.5px; font-weight: 600; }
        .mb-status-msg--ok  { background: #f0fdf4; border: 1px solid #bbf7d0; color: #16a34a; }
        .mb-status-msg--err { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }

        .mb-modal-footer { display: flex; align-items: center; justify-content: space-between; padding-top: 16px; margin-top: 14px; border-top: 1px solid #f3ecfa; }
        .mb-active-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: #16a34a; background: #f0fdf4; border: 1px solid #bbf7d0; padding: 4px 10px; border-radius: 7px; }
        .mb-upload-btn { display: inline-flex; align-items: center; gap: 8px; padding: 11px 22px; background: ${C_GRAD}; color: #fff; font-size: 13px; font-weight: 700; border: none; border-radius: 10px; cursor: pointer; box-shadow: 0 5px 18px rgba(240,77,160,0.28); transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s; font-family: inherit; }
        .mb-upload-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(240,77,160,0.4); }
        .mb-upload-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        
        .mb-preview-wrap { display: flex; flex-direction: column; gap: 12px; }
        .mb-preview-toolbar { display: flex; align-items: center; gap: 10px; }
        .mb-preview-live-dot { width: 8px; height: 8px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 0 3px rgba(74,222,128,0.25); animation: mb-pulse 2s infinite; flex-shrink: 0; }
        .mb-preview-live-txt { font-size: 12px; font-weight: 700; color: #16a34a; flex: 1; }
        .mb-open-link { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; color: ${C_PINK}; text-decoration: none; padding: 5px 12px; border-radius: 8px; background: rgba(240,77,160,0.08); border: 1px solid rgba(240,77,160,0.18); transition: background 0.2s; white-space: nowrap; }
        .mb-open-link:hover { background: rgba(240,77,160,0.14); }
        .mb-pdf-frame { width: 100%; height: 400px; border: none; border-radius: 12px; background: #f9f5ff; box-shadow: 0 4px 20px rgba(26,0,64,.08); }

        @media (max-width: 768px) {
          .mb-page { padding: 18px 14px 48px; }
          .mb-page-header { flex-direction: column; align-items: stretch; }
          .mb-manage-btn { justify-content: center; }
          .mb-search { width: 160px; }
          .mb-table th, .mb-table td { padding: 10px 12px; }
          .mb-pdf-frame { height: 320px; }
          .mb-msg-cell { max-width: 120px; }
          .mb-view-grid { grid-template-columns: 1fr; gap: 12px; padding: 20px; }
        }
      `}</style>
    </App>
  );
}